import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { AdvisorProfileResponse } from "../types/advisorProfile.types";
import { selectHasSupportedAdvisorCapability } from "./auth-slice";

// ITAM-2887: the advisor context, ported from the dev branch's
// consultPharmacyContext. An advisor has no fixed pharmacy pair (the pharmacy
// is picked per draft), so this slice carries the profile row and nothing
// else: a normal load, a post-mutation refetch, and a retry for a failed
// first load.

export type AdvisorContextStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "noProfile"
  | "error";

export interface AdvisorContextState {
  profile: AdvisorProfileResponse | null;
  status: AdvisorContextStatus;
  /** True after a fetch that FAILED (as opposed to one that found no profile). */
  loadError: boolean;
}

const initialState: AdvisorContextState = {
  profile: null,
  status: "idle",
  loadError: false,
};

const advisorContextSlice = createSlice({
  name: "advisorContext",
  initialState,
  reducers: {
    fetchStart: (state) => {
      state.status = "loading";
      state.loadError = false;
    },
    /**
     * The ONLY success path, exhaustive on purpose so a failure can never
     * masquerade as an absence: a profile is adopted, `null` (a 404) means no
     * profile row yet, which is the onboarding state the page renders.
     */
    fetchSucceeded: (
      state,
      action: PayloadAction<AdvisorProfileResponse | null>,
    ) => {
      const profile = action.payload;
      state.loadError = false;
      if (profile == null) {
        state.profile = null;
        state.status = "noProfile";
        return;
      }
      state.profile = profile;
      state.status = "loaded";
    },
    /**
     * Network, timeout, 5xx: anything that is NOT a 404. The last known
     * profile is PRESERVED deliberately: a failed request is not evidence
     * that the profile is gone, and treating it as such would disable
     * activation and discovery over a transient blip.
     */
    fetchFailed: (state) => {
      state.status = state.profile != null ? "loaded" : "error";
      state.loadError = true;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    // The context belongs to one authenticated persona and must not outlive it.
    builder.addCase("auth/logout", () => initialState);
    builder.addCase("auth/personaExit", () => initialState);
  },
});

export const advisorContextActions = advisorContextSlice.actions;

export const selectAdvisorContextState = (state: RootState) =>
  state.advisorContext;

export const selectAdvisorProfile = (state: RootState) =>
  state.advisorContext.profile;

/**
 * ONE predicate consumed by BOTH availability controls ("Aktiviraj
 * dostupnost" and the resume "Dostupan sam"). Pause is never gated: going
 * unavailable is always safe.
 */
export const selectCanActivateAvailability = createSelector(
  [selectHasSupportedAdvisorCapability, selectAdvisorProfile],
  (hasCapability, profile) => hasCapability && profile != null,
);

export default advisorContextSlice;
